import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { profilesAPI } from '../../../services/api';
import { toDate, toLocalInput, fromLocalInput, memoPresetAt } from '../../service/sopShared';
import { S, btnPrimary, btnLink, field, dueText } from './wsShared';

// 备忘录 — the open 待办 of one member, on the workspace's 下一步 tab: what was
// promised to the member and when it is due, plus a one-line quick add. A
// 备忘录 is a profile_notes row with remind_at (phase 1); done ones and plain
// 客户情况 notes live in 全部记录 · 客户情况.
//
// Props: profileId, meId, isZh, focusKey (bump → focus the quick add),
// onChanged (after a write), onOpenAll (→ 全部记录 · 客户情况).

const PRESETS = [
  ['2h', '2 小时后', 'In 2h'],
  ['tomorrow', '明天 10:00', 'Tomorrow 10:00'],
  ['48h', '48 小时后', 'In 48h'],
  ['tue', '下周二 10:00', 'Next Tue 10:00'],
  ['custom', '自选时间', 'Pick…'],
];
// 明天 10:00 / 下周二 10:00 are Pacific (sopShared.memoPresetAt).
const presetAt = memoPresetAt;
const isOpenTodo = (n) => {
  if (n.state) return n.state === 'open' || n.state === 'overdue';
  return !!n.remind_at && !n.done_at;
};

export default function MemoList({ profileId, meId, isZh = true, focusKey = 0, onChanged, onOpenAll, initialText = '' }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [rows, setRows] = useState(null);
  const [text, setText] = useState(initialText || '');
  // 操作引导 (示例模式) prefills the quick add.
  useEffect(() => { if (initialText) setText(initialText); }, [initialText]);
  const [preset, setPreset] = useState('tomorrow');
  const [custom, setCustom] = useState(() => toLocalInput(presetAt('tomorrow')));
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await profilesAPI.getProfileNotes(profileId);
      const d = r.data || {};
      setRows(Array.isArray(d.notes) ? d.notes : Array.isArray(d.items) ? d.items : []);
    } catch (e) { setRows([]); }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focusKey && inputRef.current) inputRef.current.focus(); }, [focusKey]);

  const open = (rows || []).filter(isOpenTodo)
    .sort((a, b) => (toDate(a.remind_at)?.getTime() || 0) - (toDate(b.remind_at)?.getTime() || 0));

  const add = async (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v) { inputRef.current && inputRef.current.focus(); return; }
    const at = preset === 'custom' ? fromLocalInput(custom) : presetAt(preset);  // typed as Pacific
    if (!at) { toast.error(t('请选择时间', 'Pick a time')); return; }
    setSaving(true);
    try {
      await profilesAPI.createProfileNote(profileId, { note: v, remind_at: at.toISOString(), assignee_user_id: meId || null });
      setText('');
      toast.success(t('已记下，到时间会提醒你', 'Saved — you will be reminded'));
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('保存失败', 'Could not save'));
    } finally { setSaving(false); }
  };

  const done = async (n) => {
    // eslint-disable-next-line no-alert
    const result = window.prompt(t('完成了。结果是？（选填）', 'Done. Result? (optional)'), '');
    if (result === null) return;
    setBusyId(n.id);
    try {
      await profilesAPI.markNoteDone(profileId, n.id, result.trim());
      toast.success(t('已完成', 'Done'));
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusyId(null); }
  };

  return (
    <div>
      {rows === null ? (
        <div style={{ color: S.mute, fontSize: 13, padding: '8px 0' }}>{t('加载中…', 'Loading…')}</div>
      ) : open.length ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {open.map((n) => {
            const at = toDate(n.remind_at);
            const late = (n.state === 'overdue') || (at && at.getTime() < Date.now());
            return (
              <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: `1px solid ${S.line}` }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 7, flex: '0 0 auto', background: late ? '#9E2B25' : '#A39E95' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: S.ink, lineHeight: 1.55, overflowWrap: 'anywhere' }}>{n.note || n.text}</div>
                  <div style={{ fontSize: 12, color: late ? '#9E2B25' : S.mute, marginTop: 2, fontWeight: late ? 500 : 400 }}>
                    {[dueText(n.remind_at, late, isZh), n.assignee_name ? t(`负责：${n.assignee_name}`, `Owner: ${n.assignee_name}`) : ''].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button type="button" disabled={busyId === n.id} onClick={() => done(n)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 30, padding: '0 11px', borderRadius: 2, border: `1px solid ${S.line}`, background: '#FFFDF9', color: S.ok, fontSize: 12.5, fontWeight: 500, cursor: busyId === n.id ? 'wait' : 'pointer', fontFamily: 'inherit', flex: '0 0 auto' }}>
                  <Check size={13} />{t('完成', 'Done')}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: S.mute, padding: '6px 0 10px' }}>{t('没有待办的事。答应会员的事，记在这里。', 'Nothing pending. Write down what you promised the member.')}</div>
      )}

      <form onSubmit={add} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={inputRef} value={text} maxLength={1000} onChange={(e) => setText(e.target.value)}
            placeholder={t('＋ 记一件要做的事，例如：周五前把 3 位人选的资料发给会员', '+ Something to do, e.g. send the 3 profiles by Friday')}
            style={{ ...field, flex: 1 }} />
          <button type="submit" data-tour="ws-memo-add" disabled={saving} style={btnPrimary(saving)}>
            <Plus size={14} />{saving ? t('保存中…', 'Saving…') : t('记下', 'Add')}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: S.mute }}>{t('提醒我：', 'Remind me:')}</span>
          {PRESETS.map(([k, zh, en]) => {
            const on = preset === k;
            return (
              <button key={k} type="button" onClick={() => setPreset(k)}
                style={{ height: 26, padding: '0 10px', borderRadius: 2, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1px solid ${on ? '#111111' : S.line}`, background: on ? '#EFEAE2' : '#FFFDF9', color: on ? '#111111' : S.sub }}>
                {isZh ? zh : en}
              </button>
            );
          })}
          {preset === 'custom' && (
            <>
              <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} style={{ ...field, width: 'auto', padding: '3px 8px', fontSize: 12.5 }} />
              <span style={{ fontSize: 11, fontWeight: 500, color: S.mute }}>{t('美西时间', 'PT')}</span>
            </>
          )}
          <span style={{ flex: 1 }} />
          {onOpenAll && <button type="button" onClick={onOpenAll} style={btnLink}>{t('看全部客户情况 →', 'All notes →')}</button>}
        </div>
      </form>
    </div>
  );
}
